import { Component, type SVGProps } from 'react'
import { useSmartMatchStore } from '~/store/smartMatchStore'
import { WebViewerInstance } from '@pdftron/webviewer'

declare global {
  interface Window {
    WebViewer?: {
      getInstance: (viewer?: HTMLDivElement) => WebViewerInstance
      length: number
    }
  }
}

interface SmartMatchButtonProps {
  onClick?: () => void
  isSmartMatchEnabled: boolean
}

interface SmartMatchButtonState {
  isHovered: boolean
  isActive: boolean
}

const SmartMatchIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="lucide lucide-loader-icon lucide-loader"
    {...props}
  >
    <path d="M12 2v4" />
    <path d="m16.2 7.8 2.9-2.9" />
    <path d="M18 12h4" />
    <path d="m16.2 16.2 2.9 2.9" />
    <path d="M12 18v4" />
    <path d="m4.9 19.1 2.9-2.9" />
    <path d="M2 12h4" />
    <path d="m4.9 4.9 2.9 2.9" />
  </svg>
)

export default class SmartMatchButton extends Component<
  SmartMatchButtonProps,
  SmartMatchButtonState
> {
  constructor(props: SmartMatchButtonProps) {
    super(props)
    this.state = {
      isHovered: false,
      isActive: false
    }
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown)
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
  }

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'm' || event.key === 'M') {
      this.handleClick()
    }
  }

  handleClick = () => {
    this.setState((prev) => ({ isActive: !prev.isActive }))

    const skipSmartMatchTips =
      (window.localStorage.getItem('skip_smart_match_tips') ?? 'false') === 'true'

    if (skipSmartMatchTips) {
      const isAdmin = window.location.pathname.startsWith('/admin')

      const revisedPage =
        window.WebViewer?.getInstance().Core.getDocumentViewers()[0].getCurrentPage() ?? 1

      const url = new URL(window.location.href)

      if (!isAdmin) {
        url.pathname = url.pathname.replace('/view', '/smart-match')
      } else {
        url.pathname = `${url.pathname.replace(/\/$/, '')}/smart-match`
      }

      url.searchParams.set('revised-page', revisedPage.toString())
      window.location.assign(url.toString())
      return
    }

    useSmartMatchStore.getState().setOpen(true)
  }

  render() {
    if (!this.props.isSmartMatchEnabled) return null

    const { isHovered, isActive } = this.state

    return (
      <button
        data-element="smartMatchButton"
        onClick={this.handleClick}
        onMouseEnter={() => this.setState({ isHovered: true })}
        onMouseLeave={() => this.setState({ isHovered: false })}
        style={{
          backgroundColor: isActive ? '#e64486' : isHovered ? '#FFAB40E6' : '#FFAB40',
          color: '#F8F9FA',
          borderColor: 'transparent',
          width: '32px',
          height: '32px',
          borderRadius: '4px',
          padding: '0',
          margin: '0',
          marginRight: 16,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <SmartMatchIcon />
      </button>
    )
  }
}
